import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { toast } from "sonner";
import MarkdownContent from "../components/MarkdownContent.jsx";
import {
  Sun,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  RefreshCw,
  Upload,
  Save,
  Image as ImageIcon,
  X,
} from "lucide-react";

const MAX_IMG_BYTES = 2 * 1024 * 1024; // 2 MB

export default function TarotPersonalityPage() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = user?.role === "admin";
  const chosen = user?.tarot_personality; // {gender, card_slug} or null/undefined

  const [step, setStep] = useState(1);           // 1: gender · 2: cards · 3: detail
  const [gender, setGender] = useState(null);    // "male"/"female"
  const [cards, setCards] = useState([]);        // list for current gender
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);

  // Go straight to step 3 if user has already chosen
  useEffect(() => {
    if (chosen?.card_slug) {
      setStep(3);
      setGender(chosen.gender);
      setSelectedSlug(chosen.card_slug);
    }
  }, [chosen]);

  // Load cards for a chosen gender (both wizard step 2 and step 3 need the card object)
  useEffect(() => {
    if (!gender) return;
    let cancelled = false;
    (async () => {
      setCardsLoading(true);
      try {
        const { data } = await api.get(`/tarot/personality-cards?gender=${gender}`);
        if (!cancelled) setCards(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gender]);

  const currentCard = useMemo(
    () => cards.find((c) => c.slug === selectedSlug) || null,
    [cards, selectedSlug]
  );

  const chooseGender = (g) => { setGender(g); setStep(2); };
  const backToStep = (n) => setStep(n);

  const chooseCard = async (slug) => {
    try {
      await api.put("/me/tarot-personality", { gender, card_slug: slug });
      setSelectedSlug(slug);
      await refresh();
      setStep(3);
      toast.success(t("tarot_pc.saved_toast"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const restart = async () => {
    try {
      await api.delete("/me/tarot-personality");
      await refresh();
      setSelectedSlug(null); setGender(null); setStep(1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <main data-testid="tarot-personality-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-10">
        <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2">
          <Sun size={12} /> {t("tarot.personality_eyebrow")}
        </p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">
          {t("tarot.personality_heading")}
        </h1>
        <p className="text-[#4A4238] mt-3 max-w-2xl">{t("tarot_pc.intro")}</p>
      </header>

      {/* -------- Step 1 -------- */}
      {step === 1 && (
        <section data-testid="tarot-pc-step-1" className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10">
          <p className="label-small text-[#8B3A3A] mb-4">{t("tarot_pc.step_1_eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#1A1918] mb-3">{t("tarot_pc.step_1_q")}</h2>
          <p className="text-sm text-[#4A4238] mb-8 max-w-xl">{t("tarot_pc.step_1_hint")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#C1B49A]">
            <button
              type="button"
              data-testid="tarot-pc-gender-female"
              onClick={() => chooseGender("female")}
              className="group p-8 md:p-10 bg-[#F4EEDF] hover:bg-[#EAE0CA] transition-colors text-left border-b md:border-b-0 md:border-r border-[#C1B49A]"
            >
              <Sparkles size={28} className="text-[#8B3A3A] mb-4" />
              <p className="label-small text-[#8B3A3A] mb-2">{t("tarot_pc.g_female_label")}</p>
              <p className="font-display text-3xl text-[#1A1918] mb-2">{t("tarot_pc.g_female")}</p>
              <p className="text-xs italic text-[#4A4238]">{t("tarot_pc.g_female_hint")}</p>
              <ArrowRight size={18} className="text-[#7A7061] group-hover:text-[#8B3A3A] transition-colors mt-6" />
            </button>
            <button
              type="button"
              data-testid="tarot-pc-gender-male"
              onClick={() => chooseGender("male")}
              className="group p-8 md:p-10 bg-[#F4EEDF] hover:bg-[#EAE0CA] transition-colors text-left"
            >
              <Sun size={28} className="text-[#8B3A3A] mb-4" />
              <p className="label-small text-[#8B3A3A] mb-2">{t("tarot_pc.g_male_label")}</p>
              <p className="font-display text-3xl text-[#1A1918] mb-2">{t("tarot_pc.g_male")}</p>
              <p className="text-xs italic text-[#4A4238]">{t("tarot_pc.g_male_hint")}</p>
              <ArrowRight size={18} className="text-[#7A7061] group-hover:text-[#8B3A3A] transition-colors mt-6" />
            </button>
          </div>
        </section>
      )}

      {/* -------- Step 2 -------- */}
      {step === 2 && (
        <section data-testid="tarot-pc-step-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="label-small text-[#8B3A3A] mb-2">{t("tarot_pc.step_2_eyebrow")}</p>
              <h2 className="font-display text-3xl md:text-4xl text-[#1A1918]">{t("tarot_pc.step_2_q")}</h2>
            </div>
            <button type="button" onClick={() => backToStep(1)} className="btn-ghost inline-flex items-center gap-2 self-end" data-testid="tarot-pc-back-1">
              <ArrowLeft size={14} /> {t("tarot_pc.back")}
            </button>
          </div>
          {cardsLoading ? (
            <p className="text-[#7A7061] italic">{t("tarot_pc.loading_cards")}</p>
          ) : (
            <ol className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#C1B49A]" data-testid="tarot-pc-cards-grid">
              {cards.map((c, i) => (
                <li key={c.slug}>
                  <button
                    type="button"
                    onClick={() => chooseCard(c.slug)}
                    data-testid={`tarot-pc-card-${c.slug}`}
                    className={`group w-full h-full p-5 md:p-6 bg-[#F4EEDF] hover:bg-[#EAE0CA] transition-colors text-left ${i % 4 !== 3 ? "md:border-r border-[#C1B49A]" : ""} ${i < cards.length - (cards.length % 4 === 0 ? 4 : cards.length % 4) ? "border-b border-[#C1B49A]" : ""}`}
                  >
                    <div className="aspect-[3/4] w-full bg-[#EAE0CA]/60 border border-[#C1B49A] mb-4 flex items-center justify-center overflow-hidden">
                      {c.image_base64 ? (
                        <img src={c.image_base64.startsWith("data:") ? c.image_base64 : `data:image/*;base64,${c.image_base64}`} alt={c.name_en} className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={28} className="text-[#B8A88C]" />
                      )}
                    </div>
                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#7A7061] mb-1">{String(c.order).padStart(2, "0")}</p>
                    <p className="font-display text-lg leading-tight text-[#1A1918]">
                      {lang === "nl" ? c.name_nl : c.name_en}
                    </p>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* -------- Step 3 -------- */}
      {step === 3 && currentCard && (
        <TarotCardDetail
          card={currentCard}
          isAdmin={isAdmin}
          onRestart={restart}
          onLocalUpdate={(patch) => setCards((cur) => cur.map((c) => c.slug === currentCard.slug ? { ...c, ...patch } : c))}
        />
      )}

      {step === 3 && cards.length > 0 && !currentCard && (
        <p className="text-sm italic text-[#7A7061]">{t("tarot_pc.card_missing")}</p>
      )}
    </main>
  );
}

/* ---------------- Detail + admin editor ---------------- */

function TarotCardDetail({ card, isAdmin, onRestart, onLocalUpdate }) {
  const { t, lang } = useI18n();
  const [editing, setEditing] = useState(false);
  const [descEn, setDescEn] = useState(card.description_en || "");
  const [descNl, setDescNl] = useState(card.description_nl || "");
  const [imgB64, setImgB64] = useState(card.image_base64 || "");
  const [busy, setBusy] = useState(false);

  const openEditor = () => {
    setDescEn(card.description_en || "");
    setDescNl(card.description_nl || "");
    setImgB64(card.image_base64 || "");
    setEditing(true);
  };

  const closeEditor = () => setEditing(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error(t("tarot_pc.not_image"));
      e.target.value = "";
      return;
    }
    if (f.size > MAX_IMG_BYTES) {
      toast.error(t("tarot_pc.image_too_large"));
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImgB64(String(reader.result || ""));
    reader.onerror = () => toast.error(t("tarot_pc.image_read_fail"));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { description_en: descEn, description_nl: descNl };
      if (imgB64 !== (card.image_base64 || "")) body.image_base64 = imgB64;
      const { data } = await api.put(`/tarot/personality-cards/${card.slug}`, body);
      onLocalUpdate(data);
      toast.success(t("tarot_pc.card_saved_toast"));
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cardImgSrc = card.image_base64
    ? (card.image_base64.startsWith("data:") ? card.image_base64 : `data:image/*;base64,${card.image_base64}`)
    : null;

  return (
    <section data-testid="tarot-pc-step-3">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12">
        <aside className="md:col-span-2">
          <div
            className="aspect-[3/4] w-full max-w-[420px] border border-[#C1B49A] bg-[#EAE0CA]/60 flex items-center justify-center overflow-hidden"
            data-testid="tarot-pc-card-image"
          >
            {cardImgSrc ? (
              <img src={cardImgSrc} alt={lang === "nl" ? card.name_nl : card.name_en} className="w-full h-full object-cover" />
            ) : (
              <ImageIcon size={40} className="text-[#B8A88C]" />
            )}
          </div>
          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#8B3A3A] mt-4">
            {t("tarot_pc.your_card_label")}
          </p>
          <h2 className="font-display text-4xl text-[#1A1918] mt-2" data-testid="tarot-pc-card-name">
            {lang === "nl" ? card.name_nl : card.name_en}
          </h2>
          <p className="text-xs italic text-[#7A7061] mt-2">
            {lang === "nl" ? card.name_en : card.name_nl}
          </p>
          <div className="mt-6 flex gap-2 flex-wrap">
            {isAdmin && (
              <button
                type="button"
                onClick={onRestart}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="tarot-pc-restart-btn"
              >
                <RefreshCw size={12} /> {t("tarot_pc.restart_wizard")}
              </button>
            )}
          </div>
        </aside>

        <article className="md:col-span-3">
          {!editing ? (
            <>
              <MarkdownContent testId="tarot-pc-description">
                {lang === "nl" ? card.description_nl : card.description_en}
              </MarkdownContent>
              {!(lang === "nl" ? card.description_nl : card.description_en) && (
                <p className="italic text-[#7A7061]" data-testid="tarot-pc-no-description">
                  {t("tarot_pc.no_description")}
                </p>
              )}
              {isAdmin && (
                <div className="mt-8 pt-6 border-t border-[#C1B49A]">
                  <p className="label-small text-[#8B3A3A] mb-2">{t("tarot_pc.admin_zone")}</p>
                  <button
                    type="button"
                    onClick={openEditor}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="tarot-pc-open-editor"
                  >
                    <Upload size={12} /> {t("tarot_pc.edit_this_card")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-6" data-testid="tarot-pc-editor">
              <div className="flex items-center justify-between">
                <p className="label-small text-[#8B3A3A]">{t("tarot_pc.edit_this_card")}</p>
                <button type="button" onClick={closeEditor} className="btn-ghost inline-flex items-center gap-1.5">
                  <X size={12} /> {t("tarot_pc.cancel")}
                </button>
              </div>

              <div>
                <label className="label-small block mb-2">{t("tarot_pc.field_image")}</label>
                <div className="flex items-start gap-4">
                  <div className="aspect-[3/4] w-32 border border-[#C1B49A] bg-[#EAE0CA]/60 flex items-center justify-center overflow-hidden shrink-0">
                    {imgB64 ? (
                      <img src={imgB64} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={20} className="text-[#B8A88C]" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer select-none">
                      <Upload size={12} />
                      <span>{imgB64 ? t("tarot_pc.change_image") : t("tarot_pc.choose_image")}</span>
                      <input
                        data-testid="tarot-pc-image-input"
                        type="file"
                        accept="image/*"
                        onChange={onFile}
                        className="hidden"
                      />
                    </label>
                    {imgB64 && (
                      <button
                        type="button"
                        onClick={() => setImgB64("")}
                        className="btn-ghost inline-flex items-center gap-1.5 text-[#8B3A3A] text-xs"
                      >
                        <X size={12} /> {t("tarot_pc.remove_image")}
                      </button>
                    )}
                    <p className="text-xs italic text-[#7A7061]">{t("tarot_pc.image_hint")}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="label-small block mb-2">{t("tarot_pc.field_desc_en")}</label>
                <textarea
                  data-testid="tarot-pc-desc-en"
                  rows={8}
                  value={descEn}
                  onChange={(e) => setDescEn(e.target.value)}
                  className="textarea-parchment"
                />
                <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
              </div>

              <div>
                <label className="label-small block mb-2">{t("tarot_pc.field_desc_nl")}</label>
                <textarea
                  data-testid="tarot-pc-desc-nl"
                  rows={8}
                  value={descNl}
                  onChange={(e) => setDescNl(e.target.value)}
                  className="textarea-parchment"
                />
                <p className="text-xs italic text-[#7A7061] mt-1">{t("common.markdown_hint")}</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeEditor} className="btn-secondary inline-flex items-center gap-1.5">
                  <X size={12} /> {t("tarot_pc.cancel")}
                </button>
                <button
                  type="button"
                  data-testid="tarot-pc-save-btn"
                  onClick={save}
                  disabled={busy}
                  className="btn-primary inline-flex items-center gap-1.5"
                >
                  <Save size={12} /> {busy ? t("tarot_pc.saving") : t("tarot_pc.save")}
                </button>
              </div>
            </div>
          )}
        </article>
      </div>
    </section>
  );
}
